import React from 'react';
import { UploadCloudIcon } from 'lucide-react';
import { Dialog } from './Dialog';
import { Button } from '../ui/Button';
import { Field, inputClass } from '../ui/Field';
import { InlineError } from './QueryState';
import { usePortalMutation } from '../../hooks/usePortalQuery';
import { uploadDeliverable, type DeliverableGroup } from '../../lib/portalApi';
import { formatBytes } from '../../utils/portalFormat';
interface UploadDeliverableDialogProps {
  open: boolean;
  onClose: () => void;
  orgId: string;
  projectId: string;
  /** When set, the upload becomes the next version of this deliverable. */
  versionOf: DeliverableGroup | null;
}
export function UploadDeliverableDialog({
  open,
  onClose,
  orgId,
  projectId,
  versionOf
}: UploadDeliverableDialogProps) {
  const [file, setFile] = React.useState<File | null>(null);
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [progress, setProgress] = React.useState<number | null>(null);
  const upload = usePortalMutation(uploadDeliverable);
  React.useEffect(() => {
    if (!open) return;
    setFile(null);
    setName(versionOf?.latest.name ?? '');
    setDescription('');
    setProgress(null);
    upload.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, versionOf]);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) return;
    const record = await upload.run(orgId, projectId, {
      file,
      name,
      description,
      parentId: versionOf ? versionOf.latest.parentId ?? versionOf.latest.id : null
    }, setProgress);
    if (record) onClose();else setProgress(null);
  };
  return <Dialog open={open} onClose={onClose} title={versionOf ? `New version of ${versionOf.latest.name}` : 'Upload a deliverable'} description={versionOf ? `This becomes version ${versionOf.latest.version + 1}. Earlier versions stay available.` : 'Everyone on the project is notified when it arrives.'} data-id="element-1103" data-mp="n9a7m38cpbf2">
      <form onSubmit={submit} className="space-y-5" data-id="element-1104" data-mp="1puazekwc3vcp">
        <div data-id="element-1105" data-mp="1pti024zpg84l">
          <input id="deliverable-file" type="file" className="peer sr-only" onChange={e => {
          const chosen = e.target.files?.[0] ?? null;
          setFile(chosen);
          if (chosen && !name) setName(chosen.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
        }} data-id="element-1106" data-mp="1prykkglvi13v" />
          <label htmlFor="deliverable-file" className="flex min-h-[112px] cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-dashed border-line bg-charcoal px-4 py-6 text-center transition-colors duration-150 hover:border-gold/70 peer-focus-visible:ring-2 peer-focus-visible:ring-gold" data-id="element-1107" data-mp="16w8h0oyakwnv">
            <UploadCloudIcon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-1108" data-mp="2s8ibq9ey9kw7" />
            {file ? <span className="text-[14px] text-ink" data-id="element-1109" data-mp="2x7l4ovhno0nt">
                {file.name} <span className="text-muted" data-id="element-1110" data-mp="2x5ryfhayfigc">({formatBytes(file.size)})</span>
              </span> : <span className="text-[14px] text-ink" data-id="element-1111" data-mp="lm2i5dr5owvz">
                <span className="font-medium underline underline-offset-4" data-id="element-1112" data-mp="gm7s1cf7gbbz">Choose a file</span>, up to 100 MB
              </span>}
          </label>
        </div>

        {!versionOf && <Field id="deliverable-name" label="Name" required data-id="element-1113" data-mp="6ubovmy9hv0w">
            <input id="deliverable-name" value={name} onChange={e => setName(e.target.value)} className={inputClass} maxLength={500} data-id="element-1114" data-mp="1w0ixnb384ny" />
          </Field>}
        <Field id="deliverable-note" label={versionOf ? 'What changed' : 'Description'} data-id="element-1115" data-mp="3tyrngb9luccv">
          <textarea id="deliverable-note" rows={3} value={description} onChange={e => setDescription(e.target.value)} className={`${inputClass} resize-y`} data-id="element-1116" data-mp="3p1hyewq4o2pk" />
        </Field>

        {progress !== null && <div data-id="element-1117" data-mp="1n8tsgfm3n163">
            <div className="h-1.5 overflow-hidden rounded-full bg-charcoal" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress} aria-label="Upload progress" data-id="element-1118" data-mp="1n78yar3p5k8r">
              <div className="h-full bg-gold transition-[width] duration-150 ease-out" style={{
            width: `${progress}%`
          }} data-id="element-1119" data-mp="fuzsq942e8yh" />
            </div>
            <p className="mt-1.5 text-[13px] text-muted" data-id="element-1120" data-mp="5zg0pxsmfuf0">{progress >= 100 ? 'Saving' : `Uploading, ${progress}%`}</p>
          </div>}

        <InlineError error={upload.error} data-id="element-1121" data-mp="awg94fcy199a" />
        <div className="flex gap-3" data-id="element-1122" data-mp="3ser950vjoz9n">
          <Button type="submit" disabled={!file || upload.pending} data-id="element-1123" data-mp="3sdpms092wqcm">
            {upload.pending ? 'Uploading' : 'Upload'}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-1124" data-mp="11ob5afg8kdz">
            Cancel
          </Button>
        </div>
      </form>
    </Dialog>;
}